// Frontend API client for entity system
import { API_BASE } from '../config/api';
import AuthService from './authService';

class EntityService {
  static API_ENTITIES = `${API_BASE}/api/entities`;

  // Use centralized AuthService.handleResponse for auth error handling

  // ================================================
  // CATEGORY METHODS
  // ================================================

  /**
   * Get all entity categories. Static metadata (slug/name/icon), so the
   * result is cached for the session — every FollowingTab visit was
   * refetching an unchanging list. The in-flight promise is shared so
   * concurrent callers don't double-fetch; failures aren't cached.
   */
  static _categoriesCache = null;
  static async getCategories() {
    if (EntityService._categoriesCache) return EntityService._categoriesCache;
    EntityService._categoriesCache = (async () => {
      try {
        const response = await fetch(`${EntityService.API_ENTITIES}/categories`);
        const data = await AuthService.handleResponse(response);
        return data.categories || [];
      } catch (error) {
        console.error('Error fetching categories:', error);
        EntityService._categoriesCache = null;
        return [];
      }
    })();
    return EntityService._categoriesCache;
  }

  // ================================================
  // ENTITY SEARCH & RETRIEVAL
  // ================================================

  /**
   * Search entities by category and query
   * @param {string} categorySlug - Category slug (sports, music, etc.)
   * @param {string} query - Search query
   * @param {number} limit - Max results
   */
  static async searchEntities(categorySlug, query = '', limit = 20) {
    try {
      const params = new URLSearchParams({
        category: categorySlug,
        q: query,
        limit: limit.toString()
      });

      const response = await fetch(`${EntityService.API_ENTITIES}/search?${params}`);
      const data = await AuthService.handleResponse(response);
      return data.entities || [];
    } catch (error) {
      console.error('Error searching entities:', error);
      return [];
    }
  }

  /**
   * Search entities across all categories (omits the category param).
   * Used by the unified follow-search box.
   */
  static async searchAllEntities(query = '', limit = 20) {
    try {
      const params = new URLSearchParams({ q: query, limit: limit.toString() });
      const response = await fetch(`${EntityService.API_ENTITIES}/search?${params}`);
      const data = await AuthService.handleResponse(response);
      return data.entities || [];
    } catch (error) {
      console.error('Error searching all entities:', error);
      return [];
    }
  }

  /**
   * Get entity by ID
   */
  static async getEntityById(entityId) {
    try {
      const response = await fetch(`${EntityService.API_ENTITIES}/${entityId}`);
      const data = await AuthService.handleResponse(response);
      return data.entity;
    } catch (error) {
      console.error('Error fetching entity:', error);
      return null;
    }
  }

  /**
   * Upcoming events for an entity. Sends auth headers so the server can
   * geo-filter to the viewer's home location. Returns { events, near }.
   */
  static async getEntityEvents(entityId, limit = 200) {
    try {
      const response = await fetch(
        `${EntityService.API_ENTITIES}/${encodeURIComponent(entityId)}/events?limit=${limit}`,
        { headers: AuthService.getAuthHeaders() }
      );
      const data = await AuthService.handleResponse(response);
      return { events: data.events || [], near: !!data.near };
    } catch (error) {
      console.error('Error fetching entity events:', error);
      return { events: [], near: false };
    }
  }

  /**
   * Get all entities in a category
   */
  static async getEntitiesByCategory(categorySlug, limit = 100) {
    try {
      const response = await fetch(`${EntityService.API_ENTITIES}/category/${categorySlug}?limit=${limit}`);
      const data = await AuthService.handleResponse(response);
      return data.entities || [];
    } catch (error) {
      console.error('Error fetching entities by category:', error);
      return [];
    }
  }

  // ================================================
  // USER PREFERENCE METHODS (require auth)
  // ================================================

  /**
   * Get user's entity preferences (favorites)
   */
  static async getUserPreferences(categorySlug = null) {
    try {
      let url = `${EntityService.API_ENTITIES}/user/preferences`;
      if (categorySlug) {
        url += `?category=${categorySlug}`;
      }

      const response = await fetch(url, {
        headers: AuthService.getJsonHeaders()
      });
      const data = await AuthService.handleResponse(response);
      return data.preferences || [];
    } catch (error) {
      console.error('Error fetching user preferences:', error);
      return [];
    }
  }

  /**
   * Follow suggestions for the onboarding step — entities with upcoming events
   * near the given point, excluding ones already followed. Pass the coords the
   * user just picked; omit them to use their saved home.
   * Returns { suggestions, basedOnLocation }.
   */
  static async getSuggestions({ lat = null, lng = null, limit = 12 } = {}) {
    try {
      const params = new URLSearchParams({ limit: String(limit) });
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        params.set('lat', String(lat));
        params.set('lng', String(lng));
      }

      const response = await fetch(
        `${EntityService.API_ENTITIES}/user/suggestions?${params.toString()}`,
        { headers: AuthService.getJsonHeaders() }
      );
      const data = await AuthService.handleResponse(response);
      return {
        suggestions: data.suggestions || [],
        basedOnLocation: !!data.based_on_location
      };
    } catch (error) {
      console.error('Error fetching entity suggestions:', error);
      return { suggestions: [], basedOnLocation: false };
    }
  }

  /**
   * Save user preference for an entity
   */
  static async saveUserPreference(entityId, fanLevel = 2, notifyOnEvents = true, notifyLeadTime = 7) {
    try {
      const response = await fetch(`${EntityService.API_ENTITIES}/user/preferences`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({
          entity_id: entityId,
          fan_level: fanLevel,
          notify_on_events: notifyOnEvents,
          notify_lead_time: notifyLeadTime
        })
      });

      const data = await AuthService.handleResponse(response);
      return { success: true, data };
    } catch (error) {
      console.error('Error saving user preference:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update user preference fan level
   */
  static async updateUserPreference(entityId, fanLevel) {
    try {
      const response = await fetch(`${EntityService.API_ENTITIES}/user/preferences/${entityId}`, {
        method: 'PUT',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({ fan_level: fanLevel })
      });

      const data = await AuthService.handleResponse(response);
      return { success: true, data };
    } catch (error) {
      console.error('Error updating user preference:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Delete user preference
   */
  static async deleteUserPreference(entityId) {
    try {
      const response = await fetch(`${EntityService.API_ENTITIES}/user/preferences/${entityId}`, {
        method: 'DELETE',
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      return { success: true, data };
    } catch (error) {
      console.error('Error deleting user preference:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Check if user has preference for entity
   */
  static async hasUserPreference(entityId) {
    try {
      const response = await fetch(`${EntityService.API_ENTITIES}/user/preferences/check/${entityId}`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      return data.hasPreference || false;
    } catch (error) {
      console.error('Error checking user preference:', error);
      return false;
    }
  }

  // ================================================
  // ADMIN METHODS
  // ================================================

  /**
   * Get entity statistics
   */
  static async getEntityStats() {
    try {
      const response = await fetch(`${EntityService.API_ENTITIES}/admin/stats`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      return data.stats;
    } catch (error) {
      console.error('Error fetching entity stats:', error);
      return null;
    }
  }

  /**
   * Get popular entities
   */
  static async getPopularEntities(limit = 10) {
    try {
      const response = await fetch(`${EntityService.API_ENTITIES}/admin/popular?limit=${limit}`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      return data.entities || [];
    } catch (error) {
      console.error('Error fetching popular entities:', error);
      return [];
    }
  }

  // ================================================
  // SIMILAR ENTITY METHODS
  // ================================================

  /**
   * Get similar entities for a given entity (from pre-computed relationships)
   * @param {number} entityId - Entity ID to find similar entities for
   * @param {number} minSimilarity - Minimum similarity score (0-1)
   * @param {number} limit - Max results
   */
  static async getSimilarEntities(entityId, minSimilarity = 0.5, limit = 10) {
    try {
      const params = new URLSearchParams({
        minSimilarity: minSimilarity.toString(),
        limit: limit.toString()
      });

      const response = await fetch(
        `${EntityService.API_ENTITIES}/${entityId}/similar?${params}`
      );
      const data = await AuthService.handleResponse(response);
      return data.similar || [];
    } catch (error) {
      console.error('Error fetching similar entities:', error);
      return [];
    }
  }

  /**
   * Get similar entities for all of user's favorite entities
   * Useful for recommendations - returns a map of entity_id -> similar entities
   * @param {number} minSimilarity - Minimum similarity score (0-1)
   * @param {number} limitPerEntity - Max similar entities per favorite
   */
  static async getSimilarEntitiesForUserFavorites(minSimilarity = 0.5, limitPerEntity = 5) {
    try {
      const params = new URLSearchParams({
        minSimilarity: minSimilarity.toString(),
        limit: limitPerEntity.toString()
      });

      const response = await fetch(
        `${EntityService.API_ENTITIES}/user/similar-to-favorites?${params}`,
        {
          headers: AuthService.getJsonHeaders()
        }
      );
      const data = await AuthService.handleResponse(response);
      return data.similarEntities || [];
    } catch (error) {
      console.error('Error fetching similar entities for user favorites:', error);
      return [];
    }
  }
}

export default EntityService;
